import {
  MarkdownAdapter,
  titleMiddleware,
  embedSyncedDocMiddleware,
} from '@blocksuite/affine/shared/adapters';
import type { EditorHost } from '@blocksuite/affine/std';
import type {
  BlockModel,
  BlockSnapshot,
  DocSnapshot,
  Store,
} from '@blocksuite/affine/store';
import type { Doc } from '../../doc/entities/doc';
import {
  convertSingleBlockToMarkdown,
  getAllBlocks,
} from '../utils/chat-ops-parse';

export interface BlockLineNumberInfo {
  startLineNumber: number;
  endLineNumber: number;
  blockIds: string[];
  markdownContent: string;
  confidence: 'high' | 'medium' | 'low';
}

export class BlockMarkdownLineNumberResolver {
  private readonly host: EditorHost;
  private readonly store: Store;
  private readonly doc: Doc;

  constructor(host: EditorHost, doc: Doc) {
    this.host = host;
    this.store = host.std.store;
    this.doc = doc;
  }

  /**
   * 主要方法：获取选中blocks在全文markdown中的行号
   */
  async getSelectedBlocksLineNumber(
    selectedBlockIds: string[]
  ): Promise<BlockLineNumberInfo | null> {
    try {
      // 1. 获取选中的blocks
      const selectedBlocks = selectedBlockIds
        .map(id => this.store.getBlock(id))
        .filter(Boolean) as unknown as BlockModel[];

      if (selectedBlocks.length === 0) {
        return null;
      }

      // 2. 生成选中blocks的markdown片段
      const selectedMarkdown = await convertSingleBlockToMarkdown(
        this.host,
        selectedBlocks[0].id
      );

      // 3. 生成全文markdown
      const result = await this._getAllBlocksInOrder();

      if (!result) {
        return null;
      }

      const fullDocumentMarkdown = result?.markdownContent || '';

      const allBlocks = getAllBlocks(this.doc.blockSuiteDoc);

      // 4. 在全文markdown中查找选中片段的位置
      const lineNumberInfo = await this._findMarkdownPositionInDocument(
        selectedMarkdown,
        fullDocumentMarkdown,
        selectedBlocks,
        allBlocks
      );

      return lineNumberInfo;
    } catch (error) {
      console.error('Failed to get selected blocks line number:', error);
      return null;
    }
  }

  // 根据 selectedMarkdown 和  start_line_number 查询到对应的 偏移量
  async getSelectedMarkdownMappingBlock(
    selectedMarkdown: string,
    start_line_number: number
  ): Promise<{
    matches: Array<{
      startLine: number;
      endLine: number;
      content: string;
    }>;
    offsetIndex: number;
  } | null> {
    const result = await this._getAllBlocksInOrder();
    if (!result) {
      return null;
    }
    const allBlocks: BlockSnapshot[] =
      result?.snapshot.blocks.children[1].children || [];

    const fullDocumentMarkdown = result?.markdownContent || '';

    // 1. 直接字符串匹配（高置信度）
    const directMatchResult = this._findDirectStringMatch(
      selectedMarkdown,
      fullDocumentMarkdown
    );

    if (directMatchResult && directMatchResult.matches.length) {
      const offsetIndex = directMatchResult.matches.findIndex(
        match => match.startLine === Number(start_line_number)
      );

      return {
        matches: directMatchResult.matches,
        offsetIndex,
      };
    }

    return null;
  }

  /**
   * 在全文markdown中查找选中片段的位置
   */
  private async _findMarkdownPositionInDocument(
    selectedMarkdown: string,
    fullDocumentMarkdown: string,
    selectedBlocks: BlockModel[],
    allBlocks: BlockModel[]
  ): Promise<BlockLineNumberInfo> {
    try {
      // 1. 直接字符串匹配（高置信度）
      const directMatchResult = this._findDirectStringMatch(
        selectedMarkdown,
        fullDocumentMarkdown
      );

      if (directMatchResult && directMatchResult.matches.length === 1) {
        // 只有一个匹配，直接返回
        return {
          startLineNumber: directMatchResult.matches[0].startLine,
          endLineNumber: directMatchResult.matches[0].endLine,
          blockIds: selectedBlocks.map(b => b.id),
          markdownContent: selectedMarkdown,
          confidence: 'high',
        };
      }

      // 2. 如果直接匹配有多个结果，使用block ID映射方法
      if (directMatchResult && directMatchResult.matches.length > 1) {
        const offsetIndex = await this._findPositionByBlockIdMapping(
          selectedBlocks,
          selectedMarkdown,
          allBlocks
        );

        const result = directMatchResult.matches[offsetIndex];

        if (result)
          return {
            startLineNumber: result.startLine,
            endLineNumber: result.endLine,
            blockIds: selectedBlocks.map(b => b.id),
            markdownContent: result.content,
            confidence: 'medium',
          };
      }

      // 3. Fallback：未匹配到，返回低置信度且行号为 1
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    } catch (error) {
      console.error('Failed to find markdown position in document:', error);
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    }
  }

  /**
   * 直接字符串匹配
   */
  private _findDirectStringMatch(
    selectedMarkdown: string,
    fullDocumentMarkdown: string
  ): {
    matches: Array<{ startLine: number; endLine: number; content: string }>;
  } | null {
    try {
      const fullLines = fullDocumentMarkdown.split('\n');
      const selectedLines = selectedMarkdown.split('\n');

      const matches: Array<{
        startLine: number;
        endLine: number;
        content: string;
      }> = [];

      // 在全文的每一行开始位置尝试匹配
      for (let i = 0; i <= fullLines.length - selectedLines.length; i++) {
        let match = true;

        // 检查从第i行开始的连续行是否匹配
        for (let j = 0; j < selectedLines.length; j++) {
          if (fullLines[i + j].trim() !== selectedLines[j].trim()) {
            match = false;
            break;
          }
        }

        if (match) {
          matches.push({
            startLine: i + 1, // 转换为1-based行号
            endLine: i + selectedLines.length,
            content: selectedLines.join('\n'),
          });
        }
      }

      return { matches };
    } catch (error) {
      console.error('Failed to find direct string match:', error);
      return null;
    }
  }

  /**
   * 通过block ID映射查找位置
   */
  private async _findPositionByBlockIdMapping(
    selectedBlocks: BlockModel[],
    selectedMarkdown: string,
    allBlocks: BlockModel[]
  ): Promise<number> {
    let sameMDBlocks = [];

    const selectedBlock = selectedBlocks[0];

    for (const block of allBlocks) {
      if (block.flavour === selectedBlock.flavour) {
        const blockContent = await convertSingleBlockToMarkdown(
          this.host,
          block.id
        );
        if (blockContent.trim() === selectedMarkdown.trim()) {
          sameMDBlocks.push(block);
        }
      }
    }

    return sameMDBlocks.findIndex(block => selectedBlock.id === block.id);
  }

  /**
   * 模糊匹配方法
   */
  private _findPositionByFuzzyMatching(
    selectedMarkdown: string,
    fullDocumentMarkdown: string,
    selectedBlocks: BlockModel[]
  ): BlockLineNumberInfo {
    try {
      // 使用相似度算法找到最匹配的位置
      const bestMatch = this._findBestFuzzyMatch(
        selectedMarkdown,
        fullDocumentMarkdown
      );

      return {
        startLineNumber: bestMatch.startLine,
        endLineNumber: bestMatch.endLine,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    } catch (error) {
      console.error('Failed to find position by fuzzy matching:', error);
      // 返回默认值
      return {
        startLineNumber: 1,
        endLineNumber: 1,
        blockIds: selectedBlocks.map(b => b.id),
        markdownContent: selectedMarkdown,
        confidence: 'low',
      };
    }
  }

  /**
   * 模糊匹配算法
   */
  private _findBestFuzzyMatch(
    selectedMarkdown: string,
    fullDocumentMarkdown: string
  ): { startLine: number; endLine: number } {
    try {
      const fullLines = fullDocumentMarkdown.split('\n');
      const selectedLines = selectedMarkdown.split('\n');

      let bestMatch = { startLine: 1, endLine: 1, score: 0 };

      // 滑动窗口查找最佳匹配
      for (let i = 0; i <= fullLines.length - selectedLines.length; i++) {
        let score = 0;

        for (let j = 0; j < selectedLines.length; j++) {
          if (fullLines[i + j] === selectedLines[j]) {
            score += 1;
          }
        }

        const matchScore = score / selectedLines.length;
        if (matchScore > bestMatch.score) {
          bestMatch = {
            startLine: i + 1,
            endLine: i + selectedLines.length,
            score: matchScore,
          };
        }
      }

      return {
        startLine: bestMatch.startLine,
        endLine: bestMatch.endLine,
      };
    } catch (error) {
      console.error('Failed to find best fuzzy match:', error);
      return { startLine: 1, endLine: 1 };
    }
  }

  /**
   * 获取所有blocks的有序列表
   */
  private async _getAllBlocksInOrder(): Promise<{
    markdownContent: string;
    snapshot: DocSnapshot;
  } | null> {
    const blockSuiteDoc = this.doc?.blockSuiteDoc;
    if (!blockSuiteDoc) {
      return null;
    }

    // Create transformer for document conversion
    const transformer = blockSuiteDoc.getTransformer([
      titleMiddleware(blockSuiteDoc.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ]);

    const snapshot = transformer.docToSnapshot(blockSuiteDoc);

    if (snapshot) {
      const adapter = new MarkdownAdapter(transformer, blockSuiteDoc.provider);
      const markdownResult = await adapter.fromDocSnapshot({
        snapshot,
        assets: transformer.assetsManager,
      });

      return {
        markdownContent: markdownResult.file,
        snapshot: snapshot,
      };
    }

    return null;
  }
}
